import axios from 'axios';

/**
 * Résolution de l'URL de l'API selon l'environnement :
 *
 *   1. REACT_APP_API_URL défini au build  →  utilisé tel quel (priorité absolue)
 *      ex prod : "/api"  (frontend servi par le backend, même domaine)
 *      ex perso : "https://api.mondomaine.com"
 *
 *   2. Sinon (dev local typique sans .env) :
 *      - Si on est sur localhost / 127.0.0.1 → http://localhost:5000/api
 *      - Sinon (accès depuis un autre poste du LAN : http://192.168.x.x:3000)
 *        → on dérive l'IP du hostname courant + port 5000
 *        → http://192.168.x.x:5000/api
 *
 * Conséquence : on peut rebuilder UNE FOIS et accéder à l'app depuis n'importe
 * quel poste du Wi-Fi, sans relancer un build pour chaque IP.
 */
function resolveApiBaseUrl() {
  // 1. Override explicite via env var (prod + cas custom)
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) {
    return fromEnv;
  }

  // 2. Fallback dynamique basé sur l'URL d'accès
  if (typeof window === 'undefined') {
    return 'http://localhost:5000/api';
  }
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:5000/api`;
}

const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

// ── Sélecteur global de boutique (propriétaire/admin multi-boutiques) ──
// La boutique choisie est mémorisée par navigateur ; toutes les requêtes
// GET reçoivent automatiquement ?boutique_id=<uuid>. Le backend ignore le
// paramètre pour les gérants (verrouillés sur leur boutique) et sur les
// routes non concernées. null = vue agrégée « toutes les boutiques ».
const BOUTIQUE_VUE_KEY = 'sv_boutique_vue';

export function getBoutiqueVue() {
  return localStorage.getItem(BOUTIQUE_VUE_KEY) || null;
}

export function setBoutiqueVue(boutiqueId) {
  if (boutiqueId) localStorage.setItem(BOUTIQUE_VUE_KEY, boutiqueId);
  else localStorage.removeItem(BOUTIQUE_VUE_KEY);
}

// Injecter le token + la boutique sélectionnée dans chaque requête
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sv_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Vue boutique : uniquement en lecture, uniquement sur les routes API
  // réellement filtrables par boutique, et sans écraser un choix explicite
  // de la page (ex : sélecteur local de la page Stock).
  const ROUTES_VUE_BOUTIQUE = [
    /^\/dashboard\b/, /^\/ventes-magasin\b/, /^\/caisse\b/,
    /^\/commandes\b/, /^\/commandes-client\b/, /^\/rapports\b/,
    /^\/stock\b/, /^\/vendeurs\b/,
  ];
  if ((config.method || 'get').toLowerCase() === 'get'
      && ROUTES_VUE_BOUTIQUE.some(re => re.test(config.url || ''))) {
    const bVue = getBoutiqueVue();
    if (bVue) {
      config.params = config.params || {};
      if (config.params.boutique_id === undefined && config.params.boutique === undefined) {
        config.params.boutique_id = bVue;
      }
    }
  }
  return config;
});

// Lot L21 — interception réseau pour mode offline.
// Les URL listées ci-dessous sont mises en file IndexedDB quand
// l'appareil est hors-ligne. Le helper offline-queue.js les rejoue
// au retour du réseau avec un header Idempotency-Key.
const URLS_OFFLINE_OK = [
  /^\/ventes-magasin\b/,
  /^\/caisse\/depense\b/,
  /^\/vendeurs\/[0-9a-f-]+\/sorties\b/,
  /^\/vendeurs\/sorties\/[0-9a-f-]+\/retour\b/,
];
const METHODES_OFFLINE_OK = ['POST'];

function estOffline(error) {
  return !error.response && (
    error.code === 'ERR_NETWORK' || error.message === 'Network Error'
  );
}
function estEligible(config) {
  if (!METHODES_OFFLINE_OK.includes((config.method || '').toUpperCase())) return false;
  const url = config.url || '';
  return URLS_OFFLINE_OK.some(re => re.test(url));
}

// Rafraîchir le token si expiré
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // L21 — enqueue si offline + route autorisée
    if (estOffline(error) && estEligible(original)) {
      try {
        const { enqueue } = await import('../utils/offline-queue');
        await enqueue({
          method: original.method,
          url: original.url,
          data: original.data ? JSON.parse(original.data) : null,
          headers: {},
        });
        return Promise.resolve({
          data: { _enfile: true, message: 'Enregistré localement — sync au retour du réseau' },
          status: 202,
          statusText: 'Accepted (offline)',
          headers: {},
          config: original,
        });
      } catch (e) {
        // Si IndexedDB indispo, on retombe sur l'erreur originale
      }
    }

    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !original._retry
    ) {
      original._retry = true;
      const refreshToken = localStorage.getItem('sv_refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            { refreshToken }
          );
          localStorage.setItem('sv_access_token', data.accessToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return api(original);
        } catch (_) {
          localStorage.removeItem('sv_access_token');
          localStorage.removeItem('sv_refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
